import React, { useState, useEffect } from 'react';
import { Mail, CheckCircle2, AlertCircle, RefreshCw, ExternalLink, X } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';

export function getWebmailUrl(email) {
  if (!email || typeof email !== 'string') return 'mailto:';
  const domain = email.split('@')[1]?.toLowerCase().trim() || '';
  if (domain.includes('gmail.com') || domain.includes('googlemail.com')) {
    return 'https://mail.google.com/';
  }
  if (
    domain.includes('outlook.com') ||
    domain.includes('hotmail.com') ||
    domain.includes('live.com') ||
    domain.includes('msn.com')
  ) {
    return 'https://outlook.live.com/mail/';
  }
  if (
    domain.includes('yahoo.com') ||
    domain.includes('ymail.com') ||
    domain.includes('myyahoo.com') ||
    domain.includes('rocketmail.com')
  ) {
    return 'https://mail.yahoo.com/';
  }
  if (
    domain.includes('icloud.com') ||
    domain.includes('me.com') ||
    domain.includes('mac.com')
  ) {
    return 'https://www.icloud.com/mail';
  }
  if (domain.includes('proton.me') || domain.includes('protonmail.com')) {
    return 'https://mail.proton.me/';
  }
  return `mailto:${encodeURIComponent(email)}`;
}

export function getProviderName(email) {
  if (!email || typeof email !== 'string') return 'Email';
  const domain = email.split('@')[1]?.toLowerCase().trim() || '';
  if (domain.includes('gmail.com') || domain.includes('googlemail.com')) return 'Gmail';
  if (domain.includes('outlook') || domain.includes('hotmail') || domain.includes('live')) return 'Outlook';
  if (domain.includes('yahoo') || domain.includes('ymail')) return 'Yahoo Mail';
  if (domain.includes('icloud') || domain.includes('me.com')) return 'iCloud Mail';
  if (domain.includes('proton')) return 'Proton Mail';
  return 'Webmail';
}

export default function EmailVerificationModal({ isOpen, email, onClose }) {
  const { resendConfirmation, refreshUserData } = useAuth();
  const [resending, setResending] = useState(false);
  const [resendStatus, setResendStatus] = useState(null); // 'success' | 'error' | null
  const [statusMessage, setStatusMessage] = useState('');
  const [checkingVerification, setCheckingVerification] = useState(false);
  const [cooldown, setCooldown] = useState(0);

  useEffect(() => {
    if (cooldown <= 0) return;
    const interval = setInterval(() => {
      setCooldown((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);
    return () => clearInterval(interval);
  }, [cooldown]);

  if (!isOpen) return null;

  const webmailUrl = getWebmailUrl(email);
  const providerName = getProviderName(email);

  const handleVerifyNow = async () => {
    // Open detected provider webmail in a new browser tab
    window.open(webmailUrl, '_blank', 'noopener,noreferrer');

    // Actively check if the user verified
    setCheckingVerification(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.user?.email_confirmed_at) {
        if (refreshUserData) await refreshUserData();
        if (onClose) onClose(true);
      }
    } catch (e) {
      console.warn('[EmailVerificationModal] Check session notice:', e);
    } finally {
      setTimeout(() => setCheckingVerification(false), 2000);
    }
  };

  const handleResend = async () => {
    if (!email || resending || cooldown > 0) return;
    setResending(true);
    setResendStatus(null);
    setStatusMessage('');

    try {
      await resendConfirmation(email);
      setResendStatus('success');
      setStatusMessage('Verification email resent! Please check your inbox and spam folder.');
      setCooldown(60);
    } catch (err) {
      setResendStatus('error');
      setStatusMessage(err.message || 'Unable to resend email right now. Please wait a moment.');
    } finally {
      setResending(false);
    }
  };

  const handleDismiss = () => {
    try {
      sessionStorage.setItem(`senritsux_verify_popup_dismissed_${email}`, 'true');
    } catch {}
    if (onClose) onClose(false);
  };

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 9999,
        background: 'rgba(15, 12, 45, 0.85)',
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '1.5rem',
        animation: 'authFadeIn 0.25s ease-out',
      }}
    >
      <div
        className="auth-card"
        style={{
          maxWidth: '480px',
          width: '100%',
          background: 'rgba(40, 36, 115, 0.95)',
          border: '1.5px solid rgba(219, 255, 0, 0.35)',
          boxShadow: '0 20px 50px rgba(0, 0, 0, 0.5), 0 0 30px rgba(219, 255, 0, 0.15)',
          borderRadius: '24px',
          padding: '2.5rem 2rem',
          textAlign: 'center',
          position: 'relative',
        }}
      >
        {/* Close Icon */}
        <button
          type="button"
          onClick={handleDismiss}
          style={{
            position: 'absolute',
            top: '1.2rem',
            right: '1.2rem',
            background: 'transparent',
            border: 'none',
            color: 'rgba(255, 255, 255, 0.5)',
            cursor: 'pointer',
            padding: '0.4rem',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'color 0.2s ease',
          }}
          aria-label="Close"
        >
          <X size={20} />
        </button>

        {/* Mail Icon in Lime Accent Circle */}
        <div
          style={{
            width: '64px',
            height: '64px',
            borderRadius: '50%',
            background: 'rgba(219, 255, 0, 0.15)',
            border: '2px solid #DBFF00',
            color: '#DBFF00',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 1.5rem',
            boxShadow: '0 0 20px rgba(219, 255, 0, 0.3)',
          }}
        >
          <Mail size={32} />
        </div>

        {/* Title */}
        <h2
          style={{
            fontFamily: "'Poppins', sans-serif",
            fontSize: '1.75rem',
            fontWeight: 700,
            color: '#ffffff',
            marginBottom: '0.6rem',
            letterSpacing: '-0.01em',
          }}
        >
          Verify your email
        </h2>

        {/* Description */}
        <p
          style={{
            fontSize: '0.95rem',
            lineHeight: 1.6,
            color: 'rgba(255, 255, 255, 0.8)',
            marginBottom: '1.2rem',
          }}
        >
          We have sent a verification link to your registered email address. Verify your email to activate all distribution and artist services on SenritsuX.
        </p>

        {/* Highlighted Email Address */}
        {email && (
          <div
            style={{
              background: 'rgba(255, 255, 255, 0.08)',
              border: '1px solid rgba(255, 255, 255, 0.15)',
              borderRadius: '12px',
              padding: '0.65rem 1.2rem',
              color: '#DBFF00',
              fontWeight: 600,
              fontSize: '0.95rem',
              marginBottom: '1.5rem',
              wordBreak: 'break-all',
            }}
          >
            {email}
          </div>
        )}

        {/* Resend Status Banner */}
        {resendStatus === 'success' && (
          <div
            style={{
              background: 'rgba(219, 255, 0, 0.12)',
              border: '1px solid rgba(219, 255, 0, 0.4)',
              borderRadius: '10px',
              padding: '0.75rem 1rem',
              color: '#DBFF00',
              fontSize: '0.85rem',
              marginBottom: '1.2rem',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              textAlign: 'left',
            }}
          >
            <CheckCircle2 size={16} style={{ flexShrink: 0 }} />
            <span>{statusMessage}</span>
          </div>
        )}

        {resendStatus === 'error' && (
          <div
            style={{
              background: 'rgba(255, 107, 107, 0.15)',
              border: '1px solid rgba(255, 107, 107, 0.35)',
              borderRadius: '10px',
              padding: '0.75rem 1rem',
              color: '#ff8080',
              fontSize: '0.85rem',
              marginBottom: '1.2rem',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              textAlign: 'left',
            }}
          >
            <AlertCircle size={16} style={{ flexShrink: 0 }} />
            <span>{statusMessage}</span>
          </div>
        )}

        {/* Primary Action Button: Verify Now */}
        <button
          type="button"
          onClick={handleVerifyNow}
          className="auth-btn"
          style={{
            width: '100%',
            padding: '0.85rem',
            background: '#DBFF00',
            color: '#111111',
            fontWeight: 700,
            fontSize: '1rem',
            borderRadius: '12px',
            marginBottom: '0.8rem',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '0.5rem',
            cursor: 'pointer',
            border: 'none',
            boxShadow: '0 0 20px rgba(219, 255, 0, 0.35)',
          }}
        >
          {checkingVerification ? (
            <>
              <RefreshCw size={18} className="animate-spin" /> Checking Verification...
            </>
          ) : (
            <>
              Verify Now ({providerName}) <ExternalLink size={17} />
            </>
          )}
        </button>

        {/* Secondary Action: Resend email with 60s cooldown */}
        <button
          type="button"
          onClick={handleResend}
          disabled={resending || cooldown > 0}
          style={{
            width: '100%',
            padding: '0.75rem',
            background: 'rgba(255, 255, 255, 0.08)',
            border: '1px solid rgba(255, 255, 255, 0.16)',
            color: cooldown > 0 ? 'rgba(255, 255, 255, 0.5)' : '#ffffff',
            fontWeight: 600,
            fontSize: '0.9rem',
            borderRadius: '12px',
            marginBottom: '1rem',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '0.5rem',
            cursor: resending || cooldown > 0 ? 'not-allowed' : 'pointer',
            transition: 'background 0.2s ease, border-color 0.2s ease',
          }}
        >
          {resending ? (
            <>
              <RefreshCw size={15} className="animate-spin" /> Resending...
            </>
          ) : cooldown > 0 ? (
            `Resend email (${cooldown}s)`
          ) : (
            'Resend email'
          )}
        </button>

        {/* Tertiary: I'll do it later */}
        <button
          type="button"
          onClick={handleDismiss}
          style={{
            background: 'transparent',
            border: 'none',
            color: 'rgba(255, 255, 255, 0.55)',
            fontSize: '0.88rem',
            cursor: 'pointer',
            textDecoration: 'underline',
            padding: '0.4rem',
            transition: 'color 0.2s ease',
          }}
        >
          I&apos;ll do it later
        </button>
      </div>
    </div>
  );
}
